import {
  MODE_ATTRIBUTE,
  MODES,
  COUNT_ATTRIBUTES,
  checkoutBlockReason,
  countSheetAddresses,
  deliverableItemCount,
  deliveryLineUpdates,
  parseCsv,
  employeeAttributes,
  estimateDeliveryFee,
  validateEmployeeForm,
} from '@theme/merch-delivery-logic';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const root = window.Shopify?.routes?.root ?? '/';

async function getCart() {
  const res = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error('Could not load cart');
  return res.json();
}

async function updateAttributes(attributes) {
  const res = await fetch(`${root}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes }),
  });
  if (!res.ok) throw new Error((await res.json()).description || 'Cart update failed');
  return res.json();
}

const SHEETJS = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';

// Rows (header first) from an uploaded CSV or Excel sheet. SheetJS loads only for Excel files.
async function readSheet(file) {
  if (/\.csv$/i.test(file.name) || file.type === 'text/csv') return parseCsv(await file.text());
  if (!window.XLSX) {
    await new Promise((resolve, reject) => {
      const script = Object.assign(document.createElement('script'), { src: SHEETJS, onload: resolve, onerror: reject });
      document.head.append(script);
    });
  }
  const book = window.XLSX.read(await file.arrayBuffer(), { type: 'array' });
  return window.XLSX.utils.sheet_to_json(book.Sheets[book.SheetNames[0]], { header: 1, defval: '' });
}

// Storefront API (no token needed for cart operations): the delivery address checkout opens with.
async function storefront(query, variables) {
  const res = await fetch(`${root}api/2026-10/graphql.json`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ query, variables }),
  });
  if (!res.ok) throw new Error('Could not set the delivery address');
  return (await res.json()).data;
}
const CART_ADDRESSES = `query CartAddresses($id: ID!) { cart(id: $id) { delivery { addresses { id selected address { ... on CartDeliveryAddress { company address1 } } } } } }`;
const ADD_ADDRESS = `mutation AddHub($id: ID!, $addresses: [CartSelectableAddressInput!]!) { cartDeliveryAddressesAdd(cartId: $id, addresses: $addresses) { userErrors { message } } }`;
const REMOVE_ADDRESSES = `mutation RemoveHub($id: ID!, $addressIds: [ID!]!) { cartDeliveryAddressesRemove(cartId: $id, addressIds: $addressIds) { userErrors { message } } }`;

// Shared by every instance (cart page summary and cart drawer) so one guard's re-dispatched
// click is not intercepted again by the other.
let checkoutAllowed = false;

class MerchDeliveryMode extends HTMLElement {
  // Inner elements can be replaced when the cart re-renders, so look them up each time and
  // listen on this element (event delegation) rather than on the inputs themselves.
  get employeesPanel() { return this.querySelector('.merch-delivery__employees'); }
  get inputs() { return { lagos: this.querySelector('input[name="lagos"]'), outside: this.querySelector('input[name="outside"]') }; }
  get error() { return this.querySelector('.merch-delivery__error'); }
  get estimate() { return this.querySelector('.merch-delivery__estimate'); }

  connectedCallback() {
    this.fees = { lagos: Number(this.dataset.lagosFee) || 0, outside: Number(this.dataset.outsideFee) || 0 };
    this.money = new Intl.NumberFormat('en-NG', { style: 'currency', currency: this.dataset.currency || 'NGN', maximumFractionDigits: 0 });

    // Counts save on change (blur, Enter or the spinner), so there is no separate save button.
    this.onChange = (e) => {
      if (e.target.matches('.merch-delivery__options input[type="radio"]')) this.onModeChange(e.target.value);
      else if (e.target.matches('input[name="lagos"], input[name="outside"]')) this.saveEmployees();
      else if (e.target.matches('[data-sheet-input]') && e.target.files?.[0]) this.uploadSheet(e.target.files[0]);
    };
    this.onInput = (e) => {
      if (e.target.matches('input[name="lagos"], input[name="outside"]')) this.renderEstimate();
    };
    // Once focus leaves the address counts, bring the cart's delivery lines in line.
    this.onFocusOut = (e) => {
      if (!e.target.closest?.('.merch-delivery__employees')) return;
      setTimeout(() => {
        if (!this.employeesPanel?.contains(document.activeElement)) {
          this.saveQueue = (this.saveQueue ?? Promise.resolve()).then(() => this.refreshGuard({ syncLines: true }).catch(() => {}));
        }
      }, 0);
    };
    // Drag a sheet onto the upload zone.
    this.onDrag = (e) => {
      const zone = e.target.closest?.('[data-sheet-zone]');
      if (!zone) return;
      e.preventDefault();
      if (e.type === 'drop') {
        zone.classList.remove('is-over');
        const file = e.dataTransfer?.files?.[0];
        if (file) this.uploadSheet(file);
      } else {
        zone.classList.toggle('is-over', e.type !== 'dragleave');
      }
    };
    for (const type of ['dragenter', 'dragover', 'dragleave', 'drop']) this.addEventListener(type, this.onDrag);
    this.addEventListener('change', this.onChange);
    this.addEventListener('input', this.onInput);
    this.addEventListener('focusout', this.onFocusOut);

    // Capture phase so we run before Horizon's own handlers; re-checked on every click.
    this.onDocumentClick = (e) => this.guardCheckout(e);
    this.onPageShow = (e) => {
      checkoutAllowed = false;
      if (e.persisted) this.refreshGuard().catch(() => {});
    };
    // Any other cart change (quantity, remove, add) may need the delivery lines adjusted.
    this.onCartLinesUpdate = (event) => {
      event.promise
        ?.then(({ detail } = {}) => {
          if (detail?.source === 'merch-delivery') return;
          this.saveQueue = (this.saveQueue ?? Promise.resolve()).then(() => this.refreshGuard({ syncLines: true }).catch(() => {}));
        })
        .catch(() => {});
    };
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartLinesUpdate);
    document.addEventListener('click', this.onDocumentClick, true);
    window.addEventListener('pageshow', this.onPageShow);
    this.renderEstimate();
    this.refreshGuard().catch(() => this.showError('We couldn’t load your cart. Please refresh the page.'));
  }

  disconnectedCallback() {
    for (const type of ['dragenter', 'dragover', 'dragleave', 'drop']) this.removeEventListener(type, this.onDrag);
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('input', this.onInput);
    this.removeEventListener('focusout', this.onFocusOut);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartLinesUpdate);
    document.removeEventListener('click', this.onDocumentClick, true);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  showError(message) {
    const el = this.error;
    if (!el) return;
    el.textContent = message ?? '';
    el.hidden = !message;
  }

  renderEstimate() {
    const { lagos, outside } = this.inputs;
    if (!lagos || !outside || !this.estimate) return;
    const result = validateEmployeeForm({ lagos: lagos.value, outside: outside.value });
    this.estimate.textContent = result.ok
      ? `${this.money.format(estimateDeliveryFee(result, this.fees))} for ${result.lagos + result.outside} ${result.lagos + result.outside === 1 ? 'address' : 'addresses'}`
      : '';
  }

  // Mode changes share the save queue, so a pending count save or line sync can't land after them.
  onModeChange(mode) {
    this.showError('');
    if (this.employeesPanel) this.employeesPanel.hidden = mode !== MODES.employees;
    this.saveQueue = (this.saveQueue ?? Promise.resolve()).then(() => this.#applyMode(mode));
    return this.saveQueue;
  }

  async #applyMode(mode) {
    try {
      await updateAttributes(
        mode === MODES.office
          ? { [MODE_ATTRIBUTE]: MODES.office, [COUNT_ATTRIBUTES.lagos]: '', [COUNT_ATTRIBUTES.outside]: '' }
          : { [MODE_ATTRIBUTE]: MODES.employees }
      );
      await this.refreshGuard({ syncLines: true });
    } catch (err) {
      this.showError('We couldn’t change the delivery option. Please try again.');
    }
  }

  // Saves run one after another and each reads the inputs when it starts, so a slow earlier
  // save can never overwrite newer counts.
  saveEmployees() {
    this.saveQueue = (this.saveQueue ?? Promise.resolve()).then(() => this.#saveCurrentCounts());
    return this.saveQueue;
  }

  async #saveCurrentCounts() {
    let result;
    try {
      const cart = await getCart();
      result = validateEmployeeForm({ lagos: this.inputs.lagos.value, outside: this.inputs.outside.value }, deliverableItemCount(cart));
    } catch {
      return this.showError('We couldn’t check your cart. Please try again.');
    }
    if (!result.ok) return this.showError(result.error);
    this.showError('');
    try {
      await updateAttributes(employeeAttributes(result));
      await this.refreshGuard();
    } catch (err) {
      this.showError('We couldn’t save the number of addresses. Please try again.');
    }
  }

  get variantIds() {
    return { lagos: Number(this.dataset.lagosVariant), outside: Number(this.dataset.outsideVariant) };
  }

  // POST cart/update.js (updates may be empty, just to fetch fresh section HTML) and let the cart
  // drawer / cart page re-render from the response.
  async applyCartUpdates(updates) {
    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')].map((el) => el.dataset.sectionId);
    const res = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, sections: sectionIds.join(','), sections_url: window.location.pathname }),
    });
    if (!res.ok) throw new Error('Could not update delivery in your cart');
    const updated = await res.json();
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: Object.entries(updates).map(([id, quantity]) => ({ id, quantity })),
        promise: deferred.promise,
      })
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updated),
      detail: { sections: updated.sections ?? undefined, items: updated.items, itemCount: updated.item_count, source: 'merch-delivery', didError: false },
    });
    return updated;
  }

  // Employee delivery is charged as lines of the hidden "Employee delivery" product. Bring them
  // in line with the saved choice.
  async syncDeliveryLines(cart) {
    const ids = this.variantIds;
    if (!ids.lagos || !ids.outside) return cart;
    const updates = deliveryLineUpdates(cart, ids);
    return updates ? this.applyCartUpdates(updates) : cart;
  }

  // Employee orders open checkout with our dispatch hub as the delivery address; office orders
  // get it removed so the shopper enters their own.
  async syncCheckoutAddress(cart) {
    const id = `gid://shopify/Cart/${cart.token}`;
    const hub = { company: this.dataset.hubCompany, address1: this.dataset.hubAddress1, city: this.dataset.hubCity, provinceCode: this.dataset.hubProvince, countryCode: 'NG' };
    const attributes = cart.attributes ?? {};
    const counts = validateEmployeeForm({ lagos: attributes[COUNT_ATTRIBUTES.lagos], outside: attributes[COUNT_ATTRIBUTES.outside] });
    const wantHub = attributes[MODE_ATTRIBUTE] === MODES.employees && counts.ok;
    const data = await storefront(CART_ADDRESSES, { id });
    const ours = (data?.cart?.delivery?.addresses ?? []).filter((a) => a.address?.company === hub.company && a.address?.address1 === hub.address1);
    if (wantHub && !ours.some((a) => a.selected)) {
      await storefront(ADD_ADDRESS, { id, addresses: [{ selected: true, oneTimeUse: true, address: { deliveryAddress: hub } }] });
    } else if (!wantHub && ours.length) {
      await storefront(REMOVE_ADDRESSES, { id, addressIds: ours.map((a) => a.id) });
    }
  }

  // Upload the filled address sheet: count Lagos vs elsewhere, save the counts, and add the
  // delivery lines with the sheet attached to the first one.
  uploadSheet(file) {
    this.saveQueue = (this.saveQueue ?? Promise.resolve()).then(() => this.#uploadSheet(file));
    return this.saveQueue;
  }

  async #uploadSheet(file) {
    this.showError('');
    this.classList.add('is-busy');
    try {
      let rows;
      try {
        rows = await readSheet(file);
      } catch {
        return this.showError('We couldn’t read this file. Upload the template as a CSV or Excel file.');
      }
      const counts = countSheetAddresses(rows);
      if (!counts.ok) return this.showError(counts.error);
      const cart = await getCart();
      const fits = validateEmployeeForm(counts, deliverableItemCount(cart));
      if (!fits.ok) return this.showError(fits.error);
      await updateAttributes(employeeAttributes(counts));

      const ids = this.variantIds;
      const existing = {};
      for (const item of cart.items ?? []) {
        if (item.variant_id === ids.lagos || item.variant_id === ids.outside) existing[item.key] = 0;
      }
      if (Object.keys(existing).length) await this.applyCartUpdates(existing);

      const lines = [
        { id: ids.lagos, quantity: counts.lagos },
        { id: ids.outside, quantity: counts.outside },
      ].filter((line) => line.quantity > 0);
      const form = new FormData();
      form.append('id', String(lines[0].id));
      form.append('quantity', String(lines[0].quantity));
      form.append('properties[Address sheet]', file);
      const added = await fetch(`${root}cart/add.js`, { method: 'POST', body: form, headers: { Accept: 'application/json' } });
      if (!added.ok) throw new Error((await added.json()).description || 'Could not attach the sheet');
      if (lines[1]) {
        await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [lines[1]] }),
        });
      }
      await this.applyCartUpdates({});
      await this.refreshGuard({ syncLines: true });
    } catch (err) {
      this.showError('We couldn’t add your sheet. Please try again.');
    } finally {
      this.classList.remove('is-busy');
    }
  }

  // The cart summary's "Delivery" line (cart-summary.liquid) is server-rendered; keep it current.
  renderDeliveryLine(cart) {
    const attributes = cart.attributes ?? {};
    let text = 'Added at checkout';
    if (attributes[MODE_ATTRIBUTE] === MODES.employees) {
      const counts = validateEmployeeForm({ lagos: attributes[COUNT_ATTRIBUTES.lagos], outside: attributes[COUNT_ATTRIBUTES.outside] });
      const n = counts.ok ? counts.lagos + counts.outside : 0;
      text = counts.ok ? `${this.money.format(estimateDeliveryFee(counts, this.fees))} for ${n} ${n === 1 ? 'address' : 'addresses'}` : 'Add your address sheet';
    }
    document.querySelectorAll('[data-merch-delivery-line] .merch-delivery-line__value').forEach((el) => (el.textContent = text));
  }

  // Syncing the delivery lines re-renders the cart, so it waits while the shopper is still in the
  // address-count boxes (a re-render would wipe what they are typing).
  async refreshGuard({ syncLines = !this.employeesPanel?.contains(document.activeElement) } = {}) {
    let cart = await getCart();
    try {
      if (syncLines) {
        cart = await this.syncDeliveryLines(cart);
        // A convenience: if it fails, the shopper simply types the address at checkout.
        await this.syncCheckoutAddress(cart).catch((err) => console.warn('[merch-delivery] checkout address', err));
      }
    } catch (err) {
      this.showError(err.message);
    }
    this.renderDeliveryLine(cart);
    const reason = checkoutBlockReason(cart);
    document.querySelectorAll('[name="checkout"]').forEach((b) => (b.disabled = Boolean(reason)));
    this.showError(reason);
    return reason;
  }

  async guardCheckout(event) {
    const button = event.target.closest?.('[name="checkout"]');
    if (!button || checkoutAllowed) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    let reason;
    try {
      reason = await this.refreshGuard({ syncLines: true });
    } catch {
      // Fail closed: never let checkout through unchecked.
      return this.showError('We couldn’t check your cart. Please try again.');
    }
    if (reason) {
      // In the drawer the delivery choice can be scrolled out of view; bring the message to the shopper.
      if (this.offsetParent) this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    // Let exactly this re-dispatched click through; the next click is checked again.
    checkoutAllowed = true;
    button.click();
    checkoutAllowed = false;
  }
}

customElements.define('merch-delivery-mode', MerchDeliveryMode);
